// Google Analytics utility functions
// Replace 'GA_MEASUREMENT_ID' with your actual Google Analytics 4 measurement ID

import { gameConfig } from '../config/gameConfig'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

// LocalStorage utility functions
const USER_ID_KEY = `${gameConfig.storagePrefix}_user_id`;

// Generate a GUID/UUID v4
const generateGUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Get or create a unique user ID
export const getUserId = (): string => {
  try {
    let userId = localStorage.getItem(USER_ID_KEY);
    
    if (!userId) {
      userId = generateGUID();
      localStorage.setItem(USER_ID_KEY, userId);
    }
    
    return userId;
  } catch (error) {
    console.error('Error managing user ID:', error);
    // Fallback to session-based ID if localStorage fails
    if (!window.sessionStorage.getItem(USER_ID_KEY)) {
      window.sessionStorage.setItem(USER_ID_KEY, generateGUID());
    }
    return window.sessionStorage.getItem(USER_ID_KEY) || 'anonymous';
  }
};

// Initialize Google Analytics (if not already done in HTML)
export const initGA = (measurementId: string) => {
  // gtag is loaded via script tag in index.html
  if (typeof window.gtag === 'function') {
    const userId = getUserId();
    window.gtag('config', measurementId, {
      user_id: userId,
    });
  }
};

// Track page views
export const trackPageView = (page_title: string, page_location?: string) => {
  if (typeof window.gtag === 'function') {
    const userId = getUserId();
    window.gtag('event', 'page_view', {
      page_title,
      page_location: page_location || window.location.href,
      user_id: userId,
    });
  }
};

// Track custom events
export const trackEvent = (eventName: string, parameters?: Record<string, string | number | boolean>) => {
  if (typeof window.gtag === 'function') {
    const userId = getUserId();
    window.gtag('event', eventName, {
      ...parameters,
      user_id: userId,
    });
  }
};

// Game-specific tracking functions
export const trackGameStart = (gameDate: string) => {
  trackEvent('game_start', {
    game_number: gameDate,
  });
};

export const trackGameComplete = (
  isWin: boolean, 
) => {
  trackEvent('game_complete', {
    result: isWin ? 'win' : 'loss',
    event_category: 'Game',
  });
};

export const trackGameCompleteDetailed = (
  gameNumber: number,
  isWin: boolean,
  playersRevealed: number,
  lettersGuessed: number,
  options?: {
    timeTaken?: number; // in seconds
    hintsUsed?: number;
    difficultyLevel?: string;
    gameMode?: string;
    userAgent?: string;
    screenResolution?: string;
    timestamp?: string;
  }
) => {
  const metadata: Record<string, string | number | boolean> = {};
  
  if (options?.timeTaken !== undefined) metadata.time_taken = options.timeTaken;
  if (options?.hintsUsed !== undefined) metadata.hints_used = options.hintsUsed;
  if (options?.difficultyLevel) metadata.difficulty_level = options.difficultyLevel;
  if (options?.gameMode) metadata.game_mode = options.gameMode;
  if (options?.userAgent) metadata.user_agent = options.userAgent;
  if (options?.screenResolution) metadata.screen_resolution = options.screenResolution;
  if (options?.timestamp) metadata.completion_timestamp = options.timestamp;

  trackEvent('game_complete', {
    game_number: gameNumber,
    result: isWin ? 'win' : 'loss',
    players_revealed: playersRevealed,
    letters_guessed: lettersGuessed,
    event_category: 'Game',
    ...metadata,
  });
};

export const trackClubGuess = (gameNumber: number, isCorrect: boolean, clubName: string) => {
  trackEvent('club_guess', {
    game_number: gameNumber,
    is_correct: isCorrect,
    club_name: clubName,
    event_category: 'Game',
  });
};

export const trackLetterGuess = (gameNumber: number, letter: string, isCorrect: boolean) => {
  trackEvent('letter_guess', {
    game_number: gameNumber,
    letter: letter,
    is_correct: isCorrect,
    event_category: 'Game',
  });
};

export const trackStatsView = () => {
  trackEvent('stats_view', {
    event_category: 'UI',
  });
};

export const trackSocialShare = (platform: string) => {
  trackEvent('social_share', {
    platform: platform,
    event_category: 'Social',
  });
};

export const trackEarlyClubGuess = (gameNumber: number, playersRevealed: number) => {
  trackEvent('early_club_guess', {
    game_number: gameNumber,
    players_revealed: playersRevealed,
    event_category: 'Game',
  });
};
